import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Container as TankIcon, Plus, RefreshCw, Trash2, Loader2, Upload,
  Calendar, FileText, ArrowLeft, Building2, DollarSign, Receipt,
  AlertTriangle, Save, FileSpreadsheet, Mail, Printer, TrendingUp,
} from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { downloadCsv, downloadFdaPdf, fdaPdfBlob } from "../lib/exporters";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import OffhireCostsDialog from "../components/OffhireCostsDialog";

const TABS = [
  { v: "pnl",         l: "P&L"          },
  { v: "fleet",       l: "Fleet"        },
  { v: "assignments", l: "On-hire"      },
  { v: "events",      l: "Events"       },
  { v: "tariffs",     l: "Rate Cards"   },
  { v: "fx",          l: "FX Rates"     },
  { v: "fda",         l: "Generate FDA" },
];

export default function Rentals() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "supervisor";
  const [tab, setTab] = useState("pnl");

  return (
    <div data-testid="rentals-page">
      <PageHeader
        title="Tank Rentals"
        subtitle="Meeberg tanks rented out to customers — fleet, assignments, events, monthly FDAs."
        testid="rentals-header"
      />
      <div className="border-b border-gray-200 bg-white px-6">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`rentals-tab-${t.v}`}
              className={`px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] border-b-2 transition-colors ${
                tab === t.v ? "border-[#006E9E] text-[#003147]" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>
      <div className="p-6 sm:p-8">
        {tab === "pnl"         && <PnlTab />}
        {tab === "fleet"       && <FleetTab canEdit={canEdit} />}
        {tab === "assignments" && <AssignmentsTab canEdit={canEdit} />}
        {tab === "events"      && <EventsTab canEdit={canEdit} />}
        {tab === "tariffs"     && <TariffsTab canEdit={canEdit} />}
        {tab === "fx"          && <FxTab canEdit={canEdit} />}
        {tab === "fda"         && <FdaTab />}
      </div>
    </div>
  );
}

// ─── P&L DASHBOARD ────────────────────────────────────────────────
const fmtUsd = (n) => `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtUsd2 = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PnlTab() {
  const [yyyyMm, setYyyyMm] = useState("2026-04");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rentals/dashboard?yyyy_mm=${yyyyMm}&months_back=6`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [yyyyMm]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading P&L…</div>;
  }
  const k = data.kpis;
  const colors = { revenue: "#006E9E", cost: "#F1471D", margin: "#99D420" };

  return (
    <div className="space-y-5" data-testid="pnl-dashboard">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Period</label>
          <input type="month" className="ps-input font-mono" value={yyyyMm} onChange={(e) => setYyyyMm(e.target.value)} data-testid="pnl-month" />
        </div>
        {!data.fx_posted && (
          <div className="ps-pill ps-pill-warn flex items-center gap-1.5 self-end mb-1">
            <AlertTriangle className="w-3 h-3" /> No FX rate posted for {yyyyMm} — using placeholder 1.08
          </div>
        )}
      </div>

      {/* KPI strip — Revenue split into Rental (Meeberg-comparable) + Services (pass-through) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="pnl-kpis">
        <Kpi label="Tanks" value={k.tank_count} sub="in fleet" />
        <Kpi label="Rental rev." value={fmtUsd(k.rental_revenue_usd ?? k.revenue_usd)} accent="#006E9E"
             sub={`+ $${((k.services_revenue_usd || 0) / 1000).toFixed(1)}k services`} />
        <Kpi label="Meeberg cost"
             value={fmtUsd2(k.cost_usd)}
             accent="#F1471D"
             sub={k.cost_eur_onhire > 0
               ? `€${k.cost_eur_recurring.toFixed(0)} + €${k.cost_eur_onhire.toFixed(0)} onhire`
               : `€${(k.cost_eur_per_day || 0).toFixed(0)}/day × ${k.period_days}d`} />
        <Kpi label="Rental margin" value={fmtUsd(k.margin_usd)} accent="#99D420"
             sub={`${k.margin_pct}% of rental rev.`} />
        <Kpi label="Total billed" value={fmtUsd(k.revenue_usd)} sub="FDA grand totals" />
        <Kpi label="Customers" value={k.active_customers} sub="renting" />
      </div>

      <ReconcileVsMeeberg data={data} onSaved={load} />

      {/* 6-month trend — pure rental revenue vs Meeberg lease */}
      <div className="border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-[#006E9E]" />
          <h3 className="text-xs uppercase tracking-[0.16em] font-bold text-gray-700">Rental revenue vs Meeberg cost — last 6 months</h3>
          <span className="text-[10px] text-gray-500 ml-auto">Services revenue (mobilisation / trucking / cleaning / handling) excluded — those offset trucker payments.</span>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "monospace" }} />
              <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtUsd(v)} contentStyle={{ fontSize: 12, fontFamily: "monospace" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="rental_revenue_usd" fill={colors.revenue} name="Rental revenue" />
              <Bar dataKey="cost_usd" fill={colors.cost} name="Meeberg cost" />
              <Line type="monotone" dataKey="margin_usd" stroke={colors.margin} strokeWidth={2.5} name="Rental margin" dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-customer breakdown */}
      <div className="border border-gray-200 bg-white">
        <div className="px-5 py-3 border-b border-gray-200 text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">
          Revenue by customer — {data.period}
        </div>
        <table className="ps-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th className="text-right">Tanks</th>
              <th className="text-right">Rental rev.</th>
              <th className="text-right">Services rev.</th>
              <th className="text-right">FDA total</th>
              <th className="text-right">% of rental</th>
            </tr>
          </thead>
          <tbody>
            {data.by_customer.map((c) => (
              <tr key={c.customer_code} data-testid={`pnl-row-${c.customer_code}`}>
                <td className="font-mono font-bold">{c.customer_code}</td>
                <td className="font-mono text-right">{c.tank_count}</td>
                <td className="font-mono text-right">{fmtUsd2(c.rental_revenue_usd ?? c.revenue_usd)}</td>
                <td className="font-mono text-right text-gray-500">{fmtUsd2(c.services_revenue_usd || 0)}</td>
                <td className="font-mono text-right">{fmtUsd2(c.revenue_usd)}</td>
                <td className="font-mono text-right text-gray-500">
                  {k.rental_revenue_usd ? ((c.rental_revenue_usd / k.rental_revenue_usd) * 100).toFixed(1) : "0.0"}%
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-bold">
              <td className="font-mono">TOTAL</td>
              <td className="font-mono text-right">{data.by_customer.reduce((s, c) => s + c.tank_count, 0)}</td>
              <td className="font-mono text-right">{fmtUsd2(k.rental_revenue_usd ?? k.revenue_usd)}</td>
              <td className="font-mono text-right text-gray-500">{fmtUsd2(k.services_revenue_usd || 0)}</td>
              <td className="font-mono text-right">{fmtUsd2(k.revenue_usd)}</td>
              <td className="font-mono text-right">100.0%</td>
            </tr>
            <tr>
              <td className="font-mono text-[#F1471D]">– MEEBERG COST</td>
              <td className="font-mono text-right text-gray-500">{k.tank_count}</td>
              <td className="font-mono text-right text-[#F1471D]">−{fmtUsd2(k.cost_usd)}</td>
              <td colSpan={2}></td>
              <td className="font-mono text-right text-gray-500">€{(k.cost_eur_per_day || 0).toFixed(0)}/day × {k.period_days}d</td>
            </tr>
            <tr className="bg-[#99D420]/10 font-bold">
              <td className="font-mono">RENTAL MARGIN</td>
              <td></td>
              <td className="font-mono text-right">{fmtUsd2(k.margin_usd)}</td>
              <td colSpan={2}></td>
              <td className="font-mono text-right">{k.margin_pct}%</td>
            </tr>
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-gray-100 text-[10px] text-gray-500 leading-relaxed">
          <b>Note:</b> Mobilisation, trucking, cleaning and handling fees are billed to customers but largely offset trucker / cleaner payments — they're shown as <b>Services rev.</b> separately, and excluded from the rental margin comparison since the Meeberg lease invoice doesn't cover them.
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="border border-gray-200 bg-white p-4 relative overflow-hidden">
      {accent && <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />}
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">{label}</div>
      <div className="text-2xl font-bold mt-1 font-mono" style={{ color: accent || "#003147" }}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5 font-mono">{sub}</div>}
    </div>
  );
}

// ─── Reconcile vs Meeberg ──────────────────────────────────────────
function ReconcileVsMeeberg({ data, onSaved }) {
  const recon = data.reconciliation;
  const period = data.period;
  const ourCostEur = data.kpis.cost_eur || 0;
  const [editing, setEditing] = useState(!recon);
  const [amount, setAmount] = useState(recon?.invoice_net_eur ?? "");
  const [refs, setRefs] = useState(recon?.invoice_refs || "");
  const [notes, setNotes] = useState(recon?.notes || "");
  const [saving, setSaving] = useState(false);

  // reset form when month changes
  useEffect(() => {
    setEditing(!recon);
    setAmount(recon?.invoice_net_eur ?? "");
    setRefs(recon?.invoice_refs || "");
    setNotes(recon?.notes || "");
  }, [recon, period]);

  const save = async () => {
    const v = parseFloat(amount);
    if (!v || v < 0) { toast.error("Enter a valid EUR amount"); return; }
    setSaving(true);
    try {
      await api.put("/rentals/meeberg-reconciliation", {
        month: period, invoice_net_eur: v, invoice_refs: refs, notes,
      });
      toast.success("Reconciliation saved");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete reconciliation for ${period}?`)) return;
    setSaving(true);
    try {
      await api.delete(`/rentals/meeberg-reconciliation/${period}`);
      toast.success("Removed");
      setAmount(""); setRefs(""); setNotes(""); setEditing(true);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const delta = recon ? recon.invoice_net_eur - ourCostEur : null;
  const deltaPct = recon && ourCostEur ? (delta / ourCostEur) * 100 : 0;
  const deltaColor = delta == null
    ? "#003147"
    : Math.abs(delta) <= 50
    ? "#15803D" // green — within tolerance
    : Math.abs(deltaPct) > 5
    ? "#F1471D" // red — material drift
    : "#A16207"; // amber

  return (
    <div className="border border-[#5A4E42]/20 bg-white p-5" data-testid="meeberg-recon">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5A4E42]">
            Reconcile vs Meeberg — {period}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Paste the invoice net total (EUR, ex VAT) Meeberg sent for this month. Delta vs our system is shown live.
          </div>
        </div>
        {recon && !editing && (
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} className="ps-btn ps-btn-secondary text-xs" data-testid="recon-edit">Edit</button>
            <button onClick={remove} disabled={saving} className="ps-btn ps-btn-secondary text-xs" data-testid="recon-delete">Delete</button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Invoice net (EUR)</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="ps-input font-mono" placeholder="28198.00" data-testid="recon-amount" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Invoice ref(s)</label>
            <input value={refs} onChange={(e) => setRefs(e.target.value)} className="ps-input font-mono" placeholder="LE000153 + LE000161" data-testid="recon-refs" />
          </div>
          <div className="sm:col-span-1">
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="ps-input" placeholder="optional" data-testid="recon-notes" />
          </div>
          <div className="flex gap-2 justify-end">
            {recon && <button onClick={() => setEditing(false)} className="ps-btn ps-btn-secondary text-xs">Cancel</button>}
            <button onClick={save} disabled={saving} className="ps-btn ps-btn-primary text-xs" data-testid="recon-save">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
          </div>
        </div>
      ) : recon && (
        <div className="grid sm:grid-cols-4 gap-4">
          <Kpi label="Our system" value={`€${ourCostEur.toFixed(0)}`} accent="#5A4E42" sub="calculated cost" />
          <Kpi label="Meeberg invoice" value={`€${recon.invoice_net_eur.toFixed(0)}`} accent="#003147" sub={recon.invoice_refs || ""} />
          <Kpi
            label="Delta"
            value={`${delta >= 0 ? "+" : ""}€${delta.toFixed(0)}`}
            accent={deltaColor}
            sub={`${delta >= 0 ? "+" : ""}${deltaPct.toFixed(1)}%${Math.abs(delta) <= 50 ? " · within tolerance" : Math.abs(deltaPct) > 5 ? " · material drift" : ""}`} />
          <div className="border border-gray-200 bg-gray-50 p-4 text-[11px] text-gray-600 leading-relaxed">
            <div className="font-bold text-gray-700 mb-1">Notes</div>
            {recon.notes || <span className="text-gray-400">—</span>}
            <div className="mt-2 text-[10px] text-gray-400 font-mono">
              Recorded by {recon.recorded_by} · {new Date(recon.recorded_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FLEET ────────────────────────────────────────────────────────
function FleetTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fleet, asn] = await Promise.all([
        api.get(`/rentals/fleet${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get(`/rentals/assignments?active=true`),
      ]);
      setItems(fleet.data);
      setAssignments(asn.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  // Derive on-hire customer per tank from the active assignments
  const onHireMap = new Map();
  assignments.forEach((a) => onHireMap.set(a.tank_number, a));

  const filtered = showAvailableOnly ? items.filter((t) => !onHireMap.has(t.tank_number)) : items;
  const onHireCount = items.filter((t) => onHireMap.has(t.tank_number)).length;
  const availableCount = items.length - onHireCount;
  const meebergDailyCost = items.reduce((s, t) => s + (Number(t.meeberg_cost_eur_per_day) || 0), 0);
  const meebergMonthlyCost = meebergDailyCost * 30;

  // Tanks with periodic test due in <= 90 days (compliance signal)
  const today = new Date();
  const dueSoon = items.filter((t) => {
    if (!t.due_for_test_date) return false;
    const d = new Date(t.due_for_test_date);
    const diffDays = (d - today) / 86400000;
    return diffDays <= 90;
  }).length;

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="fleet-kpis">
        <KpiCard label="Total fleet" value={items.length} accent="#003147" />
        <KpiCard label="On-hire" value={onHireCount} accent="#99D420" />
        <KpiCard label="Available" value={availableCount} accent="#006E9E" />
        <KpiCard label="Meeberg cost ~/mo" value={`€${meebergMonthlyCost.toLocaleString()}`} accent="#5A4E42" />
        <KpiCard label="Test due ≤90d" value={dueSoon} accent={dueSoon > 0 ? "#F1471D" : "#5A4E42"} muted={dueSoon === 0} />
      </div>
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex gap-3 items-center">
          <input className="ps-input font-mono w-72" placeholder="Search tank…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fleet-search" />
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={showAvailableOnly} onChange={(e) => setShowAvailableOnly(e.target.checked)} data-testid="fleet-available-only" />
            Available only
          </label>
        </div>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</button>
          {canEdit && (
            <>
              <button className="ps-btn ps-btn-primary" onClick={() => setShowAdd(true)} data-testid="fleet-add">
                <Plus className="w-4 h-4" /> Add tank
              </button>
              <button className="ps-btn ps-btn-secondary" onClick={() => setShowImport(true)} data-testid="fleet-import">
                <Upload className="w-4 h-4" /> Import Excel
              </button>
            </>
          )}
        </div>
      </div>
      {showImport && <FleetImportDialog onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {showAdd && <AddTankDialog onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="fleet-table">
          <thead><tr>
            <th>Tank</th>
            <th>Type</th>
            <th>Capacity</th>
            <th>Mfr / Year</th>
            <th>Test due</th>
            <th>€/day</th>
            <th>On-hire to</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-gray-400">No tanks match.</td></tr>}
            {!loading && filtered.map((t) => {
              const asn = onHireMap.get(t.tank_number);
              const dueDate = t.due_for_test_date ? new Date(t.due_for_test_date) : null;
              const dueSoonRow = dueDate && (dueDate - today) / 86400000 <= 90;
              const dueOverdue = dueDate && (dueDate - today) / 86400000 < 0;
              return (
                <tr key={t.id} data-testid={`fleet-row-${t.tank_number}`}>
                  <td className="font-mono font-bold">{t.tank_number}</td>
                  <td className="font-mono text-xs">{t.container_type || "—"}{t.purpose_type ? ` · ${t.purpose_type}` : ""}</td>
                  <td className="font-mono text-xs">{t.capacity_litres ? `${t.capacity_litres.toLocaleString()} L` : "—"}</td>
                  <td className="text-xs">
                    {t.manufacturer || "—"}
                    {t.building_year && <span className="text-gray-400"> · {t.building_year}</span>}
                  </td>
                  <td className="font-mono text-xs">
                    {t.due_for_test_date ? (
                      <span className={dueOverdue ? "text-red-600 font-bold" : dueSoonRow ? "text-orange-600 font-bold" : ""}>
                        {t.due_for_test_date}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="font-mono text-xs">€{t.meeberg_cost_eur_per_day}</td>
                  <td>
                    {asn ? (
                      <span className="ps-pill ps-pill-success"><span className="dot" /> {asn.customer_code}</span>
                    ) : (
                      <span className="ps-pill ps-pill-neutral"><span className="dot" /> available</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-500 font-mono">{filtered.length} of {items.length} tanks</div>
    </div>
  );
}

function KpiCard({ label, value, accent, muted }) {
  return (
    <div className={`border bg-white px-4 py-3 ${muted ? "border-gray-200 opacity-60" : "border-gray-200"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{label}</div>
      <div className="font-mono font-black text-2xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}


/**
 * Add a single tank to the fleet. Complements the bulk Excel import for
 * one-off additions (2-5 tanks) where firing up Excel is overkill. Backed
 * by ``POST /rentals/fleet`` which enforces uniqueness on tank_number and
 * writes a ``rental.fleet.create`` audit entry.
 */
function AddTankDialog({ onClose, onDone }) {
  const [form, setForm] = useState({
    tank_number: "",
    owner: "MEEBERG",
    owner_other: "",
    meeberg_cost_eur_per_day: 6.0,
    meeberg_onhire_date: "",
    meeberg_onhire_handling_eur: 125.0,
    status: "active",
    container_type: "",
    purpose_type: "",
    capacity_litres: "",
    manufacturer: "",
    building_year: "",
    due_for_test_date: "",
    max_weight_kg: "",
    tare_kg: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const setF = (patch) => setForm({ ...form, ...patch });
  const isOtherOwner = form.owner === "OTHER";
  const effectiveOwner = isOtherOwner ? form.owner_other.trim().toUpperCase() : form.owner;

  const canSubmit = form.tank_number.trim().length >= 4 && effectiveOwner && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      // Trim empties + coerce numerics so the Pydantic model doesn't reject
      // "" values on optional numeric fields.
      const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = {
        tank_number: form.tank_number.trim().toUpperCase(),
        owner: effectiveOwner,
        meeberg_cost_eur_per_day: toNum(form.meeberg_cost_eur_per_day) ?? 6.0,
        meeberg_onhire_date: form.meeberg_onhire_date || null,
        meeberg_onhire_handling_eur: toNum(form.meeberg_onhire_handling_eur) ?? 125.0,
        status: form.status,
        container_type: form.container_type || null,
        purpose_type: form.purpose_type || null,
        capacity_litres: toNum(form.capacity_litres),
        manufacturer: form.manufacturer || null,
        building_year: toNum(form.building_year),
        due_for_test_date: form.due_for_test_date || null,
        max_weight_kg: toNum(form.max_weight_kg),
        tare_kg: toNum(form.tare_kg),
        notes: form.notes || null,
      };
      // Drop nulls — Pydantic model has defaults / Optionals.
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);
      const { data } = await api.post("/rentals/fleet", payload);
      toast.success(`${data.tank_number} added to fleet (owner: ${data.owner})`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Add tank to fleet</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Tank number *</label>
              <input
                type="text"
                className="ps-input font-mono w-full"
                placeholder="e.g. MEBU1264683"
                value={form.tank_number}
                onChange={(e) => setF({ tank_number: e.target.value })}
                data-testid="add-tank-number"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Owner *</label>
              <select
                className="ps-input font-mono w-full"
                value={form.owner}
                onChange={(e) => setF({ owner: e.target.value })}
                data-testid="add-tank-owner"
              >
                <option value="MEEBERG">MEEBERG</option>
                <option value="PORTSIDE_OWN">PORTSIDE_OWN</option>
                <option value="OTHER">Other…</option>
              </select>
              {isOtherOwner && (
                <input
                  type="text"
                  className="ps-input font-mono w-full mt-2"
                  placeholder="Owner code (e.g. STOLT, GREENPORT)"
                  value={form.owner_other}
                  onChange={(e) => setF({ owner_other: e.target.value })}
                  maxLength={30}
                  data-testid="add-tank-owner-other"
                />
              )}
            </div>
          </div>

          {form.owner === "MEEBERG" && (
            <div className="grid grid-cols-3 gap-3 bg-gray-50 border border-gray-200 rounded-md p-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Meeberg daily cost (€)</label>
                <input type="number" step="0.01" className="ps-input font-mono w-full"
                       value={form.meeberg_cost_eur_per_day}
                       onChange={(e) => setF({ meeberg_cost_eur_per_day: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Onhire from Meeberg</label>
                <input type="date" className="ps-input font-mono w-full"
                       value={form.meeberg_onhire_date}
                       onChange={(e) => setF({ meeberg_onhire_date: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">One-time setup (€)</label>
                <input type="number" step="0.01" className="ps-input font-mono w-full"
                       value={form.meeberg_onhire_handling_eur}
                       onChange={(e) => setF({ meeberg_onhire_handling_eur: e.target.value })} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Container type</label>
              <input type="text" className="ps-input font-mono w-full"
                     placeholder="e.g. TANK20FT"
                     value={form.container_type}
                     onChange={(e) => setF({ container_type: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Purpose (IMO class)</label>
              <input type="text" className="ps-input font-mono w-full"
                     placeholder="e.g. T11"
                     value={form.purpose_type}
                     onChange={(e) => setF({ purpose_type: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Capacity (L)</label>
              <input type="number" className="ps-input font-mono w-full"
                     value={form.capacity_litres}
                     onChange={(e) => setF({ capacity_litres: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Manufacturer</label>
              <input type="text" className="ps-input font-mono w-full"
                     value={form.manufacturer}
                     onChange={(e) => setF({ manufacturer: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Building year</label>
              <input type="number" className="ps-input font-mono w-full"
                     value={form.building_year}
                     onChange={(e) => setF({ building_year: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Next test due</label>
              <input type="date" className="ps-input font-mono w-full"
                     value={form.due_for_test_date}
                     onChange={(e) => setF({ due_for_test_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Max weight (kg)</label>
              <input type="number" className="ps-input font-mono w-full"
                     value={form.max_weight_kg}
                     onChange={(e) => setF({ max_weight_kg: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Tare (kg)</label>
              <input type="number" className="ps-input font-mono w-full"
                     value={form.tare_kg}
                     onChange={(e) => setF({ tare_kg: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
            <input type="text" className="ps-input w-full"
                   placeholder="Optional — e.g. purchase reference, delivery batch"
                   value={form.notes}
                   maxLength={200}
                   onChange={(e) => setF({ notes: e.target.value })} />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="ps-btn ps-btn-primary" onClick={submit} disabled={!canSubmit} data-testid="add-tank-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : <><Plus className="w-4 h-4" /> Add tank</>}
          </button>
        </div>
      </div>
    </div>
  );
}



function FleetImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [customer, setCustomer] = useState("");
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/rentals/tariffs")
      .then((r) => setPartners(r.data.map((t) => ({ code: t.customer_code, legal_name: t.customer_code }))))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!file) { toast.error("Pick an Excel file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `/rentals/fleet/import${customer ? `?customer_code=${encodeURIComponent(customer)}` : ""}`;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`+${data.created_tanks} tanks · +${data.created_assignments} assignments (total fleet ${data.total_fleet})`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Import Tank Fleet</div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Excel file *</label>
            <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0])} className="ps-input" data-testid="fleet-import-file" />
            <p className="text-[10px] text-gray-500 mt-1">Expected: column A = tank number, column B = on-hire date.</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Auto-create on-hire assignments for</label>
            <select className="ps-input font-mono" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="fleet-import-customer">
              <option value="">— do not create assignments —</option>
              {partners.map((p) => <option key={p.code} value={p.code}>{p.code} — {p.legal_name}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="ps-btn ps-btn-primary" disabled={busy} onClick={submit} data-testid="fleet-import-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Upload className="w-4 h-4" /> Import</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ASSIGNMENTS ──────────────────────────────────────────────────
function AssignmentsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActive, setShowActive] = useState(true);
  const [customer, setCustomer] = useState("");
  const [partners, setPartners] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [editPickup, setEditPickup] = useState(null);
  const [costsFor, setCostsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customer) params.append("customer", customer);
      if (showActive) params.append("active", "true");
      const { data } = await api.get(`/rentals/assignments?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [customer, showActive]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/rentals/tariffs")
      .then((r) => setPartners(r.data.map((t) => ({ code: t.customer_code, legal_name: t.customer_code }))))
      .catch(() => {});
  }, []);

  const offHire = async (a) => {
    const drop = window.prompt(`Off-hire ${a.tank_number}?\nDrop-off date (YYYY-MM-DD):`, new Date().toISOString().slice(0, 10));
    if (!drop) return;
    try {
      await api.patch(`/rentals/assignments/${a.id}/off-hire?drop_off_date=${encodeURIComponent(drop)}`);
      toast.success(`${a.tank_number} off-hired`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center justify-between">
        <div className="flex gap-3 items-center">
          <select className="ps-input font-mono w-56" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="asn-customer-filter">
            <option value="">All customers</option>
            {partners.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
          </select>
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={showActive} onChange={(e) => setShowActive(e.target.checked)} data-testid="asn-active-only" />
            Active only
          </label>
        </div>
        <div className="flex gap-2 items-center">
          <button className="ps-btn ps-btn-secondary" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</button>
          {canEdit && (
            <button className="ps-btn ps-btn-primary" onClick={() => setShowNew(true)} data-testid="asn-new">
              <Plus className="w-4 h-4" /> On-hire tank
            </button>
          )}
        </div>
      </div>
      {showNew && (
        <NewAssignmentDialog
          onClose={() => setShowNew(false)}
          onDone={() => { setShowNew(false); load(); }}
        />
      )}
      {correct && (
        <CorrectAssignmentCustomerDialog
          assignment={correct}
          onClose={() => setCorrect(null)}
          onDone={() => { setCorrect(null); load(); }}
        />
      )}
      {editPickup && (
        <EditPickupDateDialog
          assignment={editPickup}
          onClose={() => setEditPickup(null)}
          onDone={() => { setEditPickup(null); load(); }}
        />
      )}
      <div className="border border-gray-200 bg-white">
        <table className="ps-table" data-testid="assignments-table">
          <thead><tr><th>Tank</th><th>Customer</th><th>Pickup</th><th>Drop-off</th><th>Location</th><th></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">No assignments.</td></tr>}
            {!loading && items.map((a) => (
              <tr key={a.id} data-testid={`asn-row-${a.tank_number}`}>
                <td className="font-mono font-bold">{a.tank_number}</td>
                <td className="font-mono text-xs">{a.customer_code}</td>
                <td className="font-mono text-xs">
                  {canEdit ? (
                    <button
                      className="underline decoration-dotted decoration-gray-300 hover:decoration-blue-500 hover:text-blue-600"
                      onClick={() => setEditPickup(a)}
                      title="Correct the pickup / on-hire date"
                      data-testid={`asn-edit-pickup-${a.tank_number}`}
                    >
                      {a.pickup_date || "—"}
                    </button>
                  ) : (a.pickup_date || "—")}
                </td>
                <td className="font-mono text-xs">{a.drop_off_date || <span className="text-[#99D420] font-bold">ON-HIRE</span>}</td>
                <td className="text-xs">{a.location || "—"}</td>
                <td className="text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    <button
                      className="ps-btn ps-btn-secondary h-7 px-3 text-xs"
                      onClick={() => setCostsFor(a)}
                      data-testid={`asn-costs-${a.tank_number}`}
                      title="Log cleaning / transport / repair costs for this rental"
                    >
                      <DollarSign className="w-3 h-3" /> Costs
                    </button>
                    {canEdit && (
                      <button
                        className="ps-btn ps-btn-secondary h-7 px-3 text-xs"
                        onClick={() => setCorrect(a)}
                        data-testid={`asn-correct-${a.tank_number}`}
                        title="Change the customer this assignment is tagged to"
                      >
                        Correct
                      </button>
                    )}
                    {!a.drop_off_date && canEdit && (
                      <button className="ps-btn ps-btn-secondary h-7 px-3 text-xs" onClick={() => offHire(a)} data-testid={`asn-offhire-${a.tank_number}`}>
                        Off-hire
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-500 font-mono">{items.length} assignments</div>
      {costsFor && (
        <OffhireCostsDialog
          assignment={costsFor}
          canEdit={canEdit}
          onClose={() => setCostsFor(null)}
        />
      )}
    </div>
  );
}

// ─── EVENTS ───────────────────────────────────────────────────────

/**
 * Dialog to on-hire a single tank. Creates the assignment first, then
 * fires optional starter events (handling, mobilisation, cleaning,
 * trucking) so the operator doesn't have to jump to the Events tab
 * afterwards. Amounts pre-fill from the customer's rate card for the
 * two fixed line items — handling & cleaning — following the same
 * convention as ``NewEventDialog``. Mobilisation & trucking are always
 * per-delivery and stay blank until typed.
 */
function NewAssignmentDialog({ onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [tanks, setTanks] = useState([]);          // free MEEBERG tanks
  const [tariffs, setTariffs] = useState([]);
  const [busyLoad, setBusyLoad] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    tank_number: "",
    customer_code: "",
    pickup_date: today,
    location: "",
    notes: "",
  });
  const [events, setEvents] = useState({
    HANDLING:     { on: false, amount: 0, currency: "USD" },
    MOBILISATION: { on: false, amount: 0, currency: "USD" },
    CLEANING:     { on: false, amount: 0, currency: "USD" },
    TRUCKING:     { on: false, amount: 0, currency: "USD" },
  });

  // Load fleet (MEEBERG) + tariffs + active assignments in parallel, then
  // filter tanks that are NOT currently on-hire.
  useEffect(() => {
    setBusyLoad(true);
    Promise.all([
      // Use rental_fleet (canonical Meeberg fleet, 172 tanks) instead of
      // containers?carrier_code=MEEBERG (only 49 — gate-event-derived subset).
      api.get("/rentals/fleet").then((r) => r.data || []),
      api.get("/rentals/tariffs").then((r) => r.data || []),
      api.get("/rentals/assignments?active=true").then((r) => r.data || []),
    ]).then(([fleet, tars, active]) => {
      const busyTanks = new Set(active.map((a) => a.tank_number));
      const free = (Array.isArray(fleet) ? fleet : [])
        .map((c) => c.tank_number)
        .filter((n) => n && !busyTanks.has(n))
        .sort();
      setTanks(free);
      setTariffs(tars);
    }).catch(() => {
      toast.error("Failed to load fleet / tariffs");
    }).finally(() => setBusyLoad(false));
  }, []);

  const tariff = tariffs.find((t) => t.customer_code === form.customer_code);

  // When customer changes: prefill event currencies + default fees.
  useEffect(() => {
    if (!tariff) return;
    setEvents((prev) => ({
      HANDLING:     { ...prev.HANDLING,     amount: tariff.handling_fee ?? 0, currency: tariff.rental_currency || "USD" },
      MOBILISATION: { ...prev.MOBILISATION, currency: tariff.rental_currency || "USD" },
      CLEANING:     { ...prev.CLEANING,     amount: tariff.cleaning_fee ?? 0, currency: tariff.rental_currency || "USD" },
      TRUCKING:     { ...prev.TRUCKING,     currency: tariff.rental_currency || "USD" },
    }));
  }, [tariff]);

  const setEv = (t, patch) => setEvents({ ...events, [t]: { ...events[t], ...patch } });

  const canSubmit = form.tank_number && form.customer_code && form.pickup_date && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      // 1) Create the assignment.
      await api.post("/rentals/assignments", {
        tank_number: form.tank_number,
        customer_code: form.customer_code,
        pickup_date: form.pickup_date,
        location: form.location || undefined,
        notes: form.notes || undefined,
      });

      // 2) Fire each opted-in starter event.
      // We deliberately don't wrap this in Promise.all so a mid-loop
      // failure doesn't leave orphan events without a clear ordering.
      const evTypes = Object.entries(events).filter(([, v]) => v.on && v.amount > 0);
      let evOk = 0;
      for (const [type, v] of evTypes) {
        try {
          await api.post("/rentals/events", {
            tank_number: form.tank_number,
            customer_code: form.customer_code,
            event_type: type,
            event_date: form.pickup_date,
            currency: v.currency,
            amount: Number(v.amount),
            notes: `Auto-created with on-hire assignment`,
          });
          evOk += 1;
        } catch (e) {
          toast.error(`${type} event failed: ${formatApiError(e)}`);
        }
      }

      toast.success(
        evOk > 0
          ? `${form.tank_number} on-hired to ${form.customer_code} · ${evOk} starter event${evOk === 1 ? "" : "s"} logged`
          : `${form.tank_number} on-hired to ${form.customer_code}`,
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">On-hire tank</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {busyLoad ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading fleet…
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Tank * <span className="text-gray-400 font-normal">({tanks.length} available)</span>
                </label>
                <select
                  className="ps-input font-mono w-full"
                  value={form.tank_number}
                  onChange={(e) => setForm({ ...form, tank_number: e.target.value })}
                  data-testid="new-asn-tank"
                >
                  <option value="">— pick an available Meeberg tank —</option>
                  {tanks.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {tanks.length === 0 && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    No unassigned Meeberg tanks in the fleet — either all are on-hire, or add tanks via Fleet → Upload CSV.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Customer *</label>
                <select
                  className="ps-input font-mono w-full"
                  value={form.customer_code}
                  onChange={(e) => setForm({ ...form, customer_code: e.target.value })}
                  data-testid="new-asn-customer"
                >
                  <option value="">— pick customer —</option>
                  {tariffs.map((t) => <option key={t.customer_code} value={t.customer_code}>{t.customer_code}</option>)}
                </select>
                {form.customer_code && !tariff && (
                  <p className="text-[11px] text-amber-700 mt-1">No tariff found for this customer — add one in Rate Cards first.</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Pickup date *</label>
                  <input
                    type="date"
                    className="ps-input font-mono w-full"
                    value={form.pickup_date}
                    onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                    data-testid="new-asn-pickup"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Location</label>
                  <input
                    type="text"
                    className="ps-input w-full"
                    placeholder="e.g. Tema depot"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    data-testid="new-asn-location"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
                <input
                  type="text"
                  className="ps-input w-full"
                  placeholder="Optional"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  maxLength={200}
                  data-testid="new-asn-notes"
                />
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-1">Starter charges (optional)</div>
                  <div className="text-[11px] text-gray-500">
                    Tick to log a charge at the same time as the on-hire. Handling &amp; cleaning pre-fill from the customer&apos;s rate card.
                  </div>
                </div>
                {["HANDLING", "MOBILISATION", "CLEANING", "TRUCKING"].map((t) => {
                  const v = events[t];
                  return (
                    <div key={t} className="flex items-center gap-3">
                      <label className="flex items-center gap-2 min-w-[130px]">
                        <input
                          type="checkbox"
                          checked={v.on}
                          onChange={(e) => setEv(t, { on: e.target.checked })}
                          data-testid={`new-asn-ev-${t.toLowerCase()}-on`}
                        />
                        <span className="text-xs font-semibold uppercase tracking-wider">{t}</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="ps-input font-mono flex-1 disabled:bg-gray-50 disabled:text-gray-400"
                        placeholder={t === "MOBILISATION" || t === "TRUCKING" ? "per delivery" : "0.00"}
                        value={v.amount || ""}
                        disabled={!v.on}
                        onChange={(e) => setEv(t, { amount: parseFloat(e.target.value) || 0 })}
                        data-testid={`new-asn-ev-${t.toLowerCase()}-amount`}
                      />
                      <span className="text-xs font-mono text-gray-500 w-10">{v.currency}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="ps-btn ps-btn-primary"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="new-asn-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> On-hiring…</> : <><Plus className="w-4 h-4" /> On-hire tank</>}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Correct-customer dialog for a rental assignment. Two-step UX (Preview →
 * Apply) mirrors the "Correct carrier" dialog we ship for gate events.
 * Calls PATCH /rentals/assignments/{id}/customer — the endpoint cascades
 * the change to all rental_events for the same tank in the assignment's
 * active window and writes a rental.assignment.correct_customer audit row.
 */
function CorrectAssignmentCustomerDialog({ assignment, onClose, onDone }) {
  const [tariffs, setTariffs] = useState([]);
  const [newCustomer, setNewCustomer] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busyPreview, setBusyPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/rentals/tariffs").then((r) => setTariffs(r.data || [])).catch(() => {});
  }, []);

  if (!assignment) return null;

  const current = assignment.customer_code || "";
  const canSubmit = newCustomer && newCustomer.toUpperCase() !== current.toUpperCase();

  const runPreview = async () => {
    setErr("");
    setBusyPreview(true);
    try {
      const { data } = await api.patch(
        `/rentals/assignments/${assignment.id}/customer`,
        { apply: false, new_customer_code: newCustomer.toUpperCase(), note: note || undefined },
      );
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyPreview(false);
    }
  };

  const apply = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/rentals/assignments/${assignment.id}/customer`,
        { apply: true, new_customer_code: newCustomer.toUpperCase(), note: note || undefined },
      );
      toast.success(
        `${assignment.tank_number}: ${current} → ${data.new_customer}${data.events_retagged ? ` · ${data.events_retagged} event${data.events_retagged === 1 ? "" : "s"} re-tagged` : ""}`,
      );
      onDone();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Admin correction</div>
            <div className="text-sm font-bold text-gray-900">Change customer on assignment</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-3 bg-gray-50 border border-gray-200 rounded-md p-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">Tank</div>
              <div className="font-mono font-bold mt-0.5">{assignment.tank_number}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">Pickup</div>
              <div className="font-mono mt-0.5">{assignment.pickup_date || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">Status</div>
              <div className="mt-0.5">{assignment.drop_off_date ? assignment.drop_off_date : <span className="text-[#4A8500] font-bold">ON-HIRE</span>}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 font-mono mb-1">Currently</label>
              <div className="border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono font-bold">
                {current || <span className="text-gray-400 font-normal">—</span>}
              </div>
            </div>
            <div className="text-gray-400 shrink-0 mt-5">→</div>
            <div className="flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 font-mono mb-1">Change to</label>
              <select
                value={newCustomer}
                onChange={(e) => { setNewCustomer(e.target.value); setPreview(null); }}
                className="ps-input w-full font-mono font-bold"
                data-testid="correct-cust-select"
              >
                <option value="">— pick customer —</option>
                {tariffs.map((t) => <option key={t.customer_code} value={t.customer_code}>{t.customer_code}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 font-mono mb-1">Reason (for audit trail)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="ps-input w-full"
              placeholder="e.g. Mixed up with Portal at on-hire"
              maxLength={200}
              data-testid="correct-cust-reason"
            />
          </div>

          {preview && preview.applied === false && preview.reason !== "already_at_target" && (
            <div className="border border-blue-200 bg-blue-50 rounded-md p-3 text-xs" data-testid="correct-cust-preview">
              <div className="font-bold text-blue-900 mb-1">Ready to apply</div>
              <div className="text-blue-900/80">
                Assignment will be re-tagged from <b>{preview.old_customer}</b> to <b>{preview.new_customer}</b>.
                {preview.events_to_retag > 0 && (
                  <> {preview.events_to_retag} related event{preview.events_to_retag === 1 ? "" : "s"} (handling / cleaning / trucking / mobilisation) will also be re-tagged.</>
                )}
              </div>
            </div>
          )}

          {err && (
            <div className="border border-red-200 bg-red-50 text-red-800 rounded-md p-3 text-xs">{err}</div>
          )}

          <div className="text-[11px] text-gray-500 border-l-2 border-gray-300 pl-3">
            Cascades to any rental events (handling, cleaning, mobilisation, trucking) already logged for this tank under the old customer. An audit entry is written recording who made the change and why.
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={busy || busyPreview}>Cancel</button>
          {!preview ? (
            <button
              onClick={runPreview}
              disabled={!canSubmit || busyPreview}
              className="ps-btn ps-btn-secondary"
              data-testid="preview-correct-cust"
            >
              {busyPreview ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : "Preview change"}
            </button>
          ) : (
            <button
              onClick={apply}
              disabled={busy || preview.applied === false && preview.reason === "already_at_target"}
              className="ps-btn ps-btn-primary"
              data-testid="apply-correct-cust"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : "Apply correction"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function EditPickupDateDialog({ assignment, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [pickup, setPickup] = useState(assignment.pickup_date || today);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!pickup) { toast.error("Pickup date required"); return; }
    setBusy(true);
    try {
      const url = `/rentals/assignments/${assignment.id}/pickup-date?pickup_date=${encodeURIComponent(pickup)}`;
      const { data } = await api.patch(url);
      if (data.applied) {
        toast.success(`${assignment.tank_number}: ${data.old_pickup_date || "—"} → ${data.new_pickup_date}`);
      } else {
        toast.info("Pickup date unchanged");
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-pickup-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Correct pickup date</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-gray-600">
            <div className="font-mono font-bold text-sm text-gray-900">{assignment.tank_number}</div>
            <div>on-hire to <span className="font-mono">{assignment.customer_code}</span></div>
            <div className="mt-1">Current pickup: <span className="font-mono">{assignment.pickup_date || "—"}</span></div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">New pickup / on-hire date</label>
            <input
              type="date"
              className="ps-input font-mono w-full"
              value={pickup}
              max={today}
              onChange={(e) => setPickup(e.target.value)}
              data-testid="edit-pickup-input"
            />
          </div>
          <div className="text-[11px] text-gray-500 leading-snug">
            Use this when a bulk import parsed the wrong column (e.g. periodic-test date)
            or the on-hire date was typo&apos;d. Only the assignment record changes — events
            and billing continue from the corrected date.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="ps-btn ps-btn-primary" onClick={save} disabled={busy || !pickup} data-testid="edit-pickup-save">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


function EventsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rentals/events");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-500 font-mono">{items.length} events logged</span>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</button>
          {canEdit && <button className="ps-btn ps-btn-primary" onClick={() => setShowNew(true)} data-testid="events-new"><Plus className="w-4 h-4" /> New event</button>}
        </div>
      </div>
      {showNew && <NewEventDialog onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
      <div className="border border-gray-200 bg-white">
        <table className="ps-table" data-testid="events-table">
          <thead><tr><th>Date</th><th>Tank</th><th>Customer</th><th>Type</th><th>Currency</th><th className="text-right">Amount</th><th>Notes</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-gray-400">No events yet.</td></tr>}
            {!loading && items.map((e) => (
              <tr key={e.id}>
                <td className="font-mono text-xs">{e.event_date}</td>
                <td className="font-mono">{e.tank_number}</td>
                <td className="font-mono text-xs">{e.customer_code}</td>
                <td className="text-xs">{e.event_type}</td>
                <td className="font-mono text-xs">{e.currency}</td>
                <td className="font-mono text-right">{Number(e.amount).toFixed(2)}</td>
                <td className="text-xs text-gray-500">{e.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewEventDialog({ onClose, onDone }) {
  const [form, setForm] = useState({
    tank_number: "", customer_code: "", event_type: "HANDLING",
    event_date: new Date().toISOString().slice(0, 10),
    currency: "USD", amount: 0, notes: "",
  });
  const [tariffs, setTariffs] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/rentals/tariffs")
      .then((r) => setTariffs(r.data || []))
      .catch(() => {});
    // Load the Meeberg-owned tank fleet for the tank-number autocomplete.
    // Sourced from the containers cache filtered to carrier_code=MEEBERG —
    // avoids typos and constrains operators to real Meeberg metal.
    api.get("/containers?carrier_code=MEEBERG&limit=2000")
      .then((r) => setFleet(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFleet([]));
  }, []);

  // Auto-fill amount + currency from the customer's rate card whenever
  // customer or event_type changes — but ONLY for truly fixed line items.
  // Mobilisation is per-delivery (location-dependent) and must always be
  // typed by the operator to avoid billing surprises.
  useEffect(() => {
    if (!form.customer_code || !tariffs.length) return;
    const t = tariffs.find((x) => x.customer_code === form.customer_code);
    if (!t) return;
    const feeByType = {
      HANDLING: t.handling_fee,
      CLEANING: t.cleaning_fee,
      // MOBILISATION intentionally omitted — always variable per delivery.
    };
    const auto = feeByType[form.event_type];
    setForm((f) => ({
      ...f,
      amount: auto !== undefined ? auto : 0,   // reset to 0 when variable
      currency: t.rental_currency || f.currency,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_code, form.event_type, tariffs]);

  const partners = tariffs.map((t) => ({ code: t.customer_code, legal_name: t.customer_code }));

  const submit = async () => {
    if (!form.tank_number || !form.customer_code || !form.amount) { toast.error("Fill all fields"); return; }
    setBusy(true);
    try {
      await api.post("/rentals/events", form);
      toast.success("Event logged");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">New rental event</div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <input className="ps-input font-mono col-span-2" placeholder="Tank number (pick from Meeberg fleet)" value={form.tank_number} onChange={(e) => set("tank_number", e.target.value.toUpperCase())} list="rental-fleet-tanks" autoComplete="off" data-testid="event-tank" />
          <datalist id="rental-fleet-tanks">
            {fleet.map((c) => (
              <option key={c.container_number} value={c.container_number} label={c.iso_type || ""} />
            ))}
          </datalist>
          <select className="ps-input font-mono col-span-2" value={form.customer_code} onChange={(e) => set("customer_code", e.target.value)} data-testid="event-customer">
            <option value="">— customer —</option>
            {partners.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
          </select>
          <select className="ps-input font-mono" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="event-type">
            <option value="HANDLING">Handling / Lift</option>
            <option value="MOBILISATION">Mobilisation (trucking to site)</option>
            <option value="CLEANING">Cleaning</option>
          </select>
          <input type="date" className="ps-input font-mono" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} />
          <select className="ps-input font-mono" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
          <input className="ps-input font-mono" type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => set("amount", parseFloat(e.target.value) || 0)} data-testid="event-amount" />
          <div className="col-span-2 -mt-1 text-[10px] text-gray-500">
            Handling &amp; cleaning auto-fill from the customer&apos;s rate card. Mobilisation is per-delivery — type the trucking cost for this specific site.
          </div>
          <textarea className="ps-input col-span-2" rows={2} placeholder="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="ps-btn ps-btn-primary" disabled={busy} onClick={submit} data-testid="event-save">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TARIFFS ──────────────────────────────────────────────────────
function TariffsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [partners, setPartners] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/rentals/tariffs");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); api.get("/partners?role=customer").then((r) => setPartners(r.data)); }, []);

  const save = async () => {
    try {
      await api.put(`/rentals/tariffs/${editing.customer_code}`, editing);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><span className="text-xs text-gray-500">{items.length} customers configured</span>
        {canEdit && <button className="ps-btn ps-btn-primary" onClick={() => setEditing({ customer_code: "", daily_rate: 20, rental_currency: "USD", handling_fee: 50, cleaning_fee: 350 })} data-testid="tariff-new"><Plus className="w-4 h-4" /> New rate card</button>}
      </div>
      <div className="border border-gray-200 bg-white">
        <table className="ps-table"><thead><tr><th>Customer</th><th>Daily rate</th><th>Currency</th><th>Handling</th><th>Cleaning</th><th>Free days</th><th>Step-down</th><th></th></tr></thead><tbody>
          {items.map((t) => (
            <tr key={t.customer_code} data-testid={`tariff-row-${t.customer_code}`}>
              <td className="font-mono font-bold">{t.customer_code}</td>
              <td className="font-mono">{t.daily_rate}</td>
              <td className="font-mono text-xs">{t.rental_currency}</td>
              <td className="font-mono">${t.handling_fee}</td>
              <td className="font-mono">${t.cleaning_fee}</td>
              <td className="font-mono">{t.free_days || 0}</td>
              <td className="font-mono text-xs">
                {t.step_down_after_days && t.step_down_daily_rate
                  ? `after ${t.step_down_after_days}d → ${t.rental_currency === "EUR" ? "€" : "$"}${t.step_down_daily_rate}/d`
                  : "—"}
              </td>
              <td className="text-right">{canEdit && <button className="ps-btn ps-btn-secondary h-7 px-3 text-xs" onClick={() => setEditing({ ...t })}>Edit</button>}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white border border-gray-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{editing.customer_code ? `Rate Card — ${editing.customer_code}` : "New Rate Card"}</div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <select className="ps-input font-mono col-span-2" value={editing.customer_code} onChange={(e) => setEditing({ ...editing, customer_code: e.target.value })} disabled={items.find((x) => x.customer_code === editing.customer_code)} data-testid="tariff-customer">
                <option value="">— customer —</option>
                {partners.map((p) => <option key={p.code} value={p.code}>{p.code} — {p.legal_name}</option>)}
              </select>
              <input className="ps-input font-mono" type="number" step="0.01" placeholder="Daily rate" value={editing.daily_rate} onChange={(e) => setEditing({ ...editing, daily_rate: parseFloat(e.target.value) || 0 })} data-testid="tariff-daily-rate" />
              <select className="ps-input font-mono" value={editing.rental_currency} onChange={(e) => setEditing({ ...editing, rental_currency: e.target.value })}><option value="USD">USD</option><option value="EUR">EUR</option></select>
              <input className="ps-input font-mono" type="number" placeholder="Handling fee" value={editing.handling_fee} onChange={(e) => setEditing({ ...editing, handling_fee: parseFloat(e.target.value) || 0 })} />
              <input className="ps-input font-mono" type="number" placeholder="Cleaning fee" value={editing.cleaning_fee} onChange={(e) => setEditing({ ...editing, cleaning_fee: parseFloat(e.target.value) || 0 })} />
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-gray-600 mb-1">
                  Free rental days per tank
                </label>
                <input
                  className="ps-input font-mono w-full"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 180 = 6 months free"
                  value={editing.free_days ?? 0}
                  onChange={(e) => setEditing({ ...editing, free_days: parseInt(e.target.value, 10) || 0 })}
                  data-testid="tariff-free-days"
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  Days from each tank's pickup date before daily rate kicks in. 0 = charge from day 1.
                  Resets per assignment.
                </div>
              </div>
              <div className="col-span-2 border-t border-gray-100 pt-3">
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-gray-600 mb-1">
                  Step-down to reduced rate
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="ps-input font-mono"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="After N days (e.g. 180)"
                    value={editing.step_down_after_days ?? 0}
                    onChange={(e) => setEditing({ ...editing, step_down_after_days: parseInt(e.target.value, 10) || 0 })}
                    data-testid="tariff-stepdown-after-days"
                  />
                  <input
                    className="ps-input font-mono"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Reduced rate (e.g. 15)"
                    value={editing.step_down_daily_rate ?? 0}
                    onChange={(e) => setEditing({ ...editing, step_down_daily_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="tariff-stepdown-daily-rate"
                  />
                </div>
                <div className="text-[11px] text-gray-500 mt-1">
                  After this many days from pickup, daily rate drops to the reduced rate. Counts from pickup date, independent of free days. 0/0 disables step-down.
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button className="ps-btn ps-btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="ps-btn ps-btn-primary" onClick={save} data-testid="tariff-save"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FX RATES ─────────────────────────────────────────────────────
function FxTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rate, setRate] = useState(1.08);

  const load = async () => {
    try { const { data } = await api.get("/rentals/fx-rates"); setItems(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Highlight the last 12 months that are missing a posted rate — operators
  // generating back-dated FDAs for ITT (or any EUR customer) will see a blank
  // "USD equivalent" column if the rate for that month isn't in the system.
  const missingMonths = (() => {
    const posted = new Set(items.map((f) => f.month));
    const now = new Date();
    const out = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!posted.has(m)) out.push(m);
    }
    return out;
  })();

  // Last-known rate so the input prefills with something sensible rather
  // than the hard-coded 1.08.
  useEffect(() => {
    if (items.length && rate === 1.08) {
      setRate(parseFloat(items[0].eur_to_usd) || 1.08);
    }
  }, [items, rate]);

  const save = async () => {
    try {
      await api.put(`/rentals/fx-rates/${month}`, { month, eur_to_usd: rate });
      toast.success(`${month} → ${rate}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postMissing = async (m) => {
    const last = items[0]?.eur_to_usd || 1.08;
    const r = window.prompt(`Post EUR → USD rate for ${m}?\n(Press OK to keep ${last}, or type a different rate.)`, String(last));
    if (!r) return;
    const parsed = parseFloat(r);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Rate must be a positive number");
      return;
    }
    try {
      await api.put(`/rentals/fx-rates/${m}`, { month: m, eur_to_usd: parsed });
      toast.success(`${m} → ${parsed}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      {missingMonths.length > 0 && (
        <div className="border-l-4 border-amber-400 bg-amber-50 p-4" data-testid="fx-missing-months">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {missingMonths.length} month{missingMonths.length === 1 ? "" : "s"} missing a FX rate
          </div>
          <div className="text-xs text-amber-900/80 mb-3">
            FDAs for EUR customers (ITT, …) in these months will show blank "USD equivalent" columns. Click any month to post a rate for it.
          </div>
          <div className="flex flex-wrap gap-1.5">
            {missingMonths.map((m) => (
              <button
                key={m}
                onClick={() => canEdit && postMissing(m)}
                disabled={!canEdit}
                className="px-2 py-1 text-[11px] font-mono bg-white border border-amber-300 rounded hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={`fx-post-${m}`}
                title={canEdit ? `Post the rate for ${m}` : "View-only — ask a rentals manager to post"}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="border border-gray-200 bg-white p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Post month-end EUR → USD rate</div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Month</label>
            <input type="month" className="ps-input font-mono" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="fx-month" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">1 EUR =</label>
            <input type="number" step="0.0001" className="ps-input font-mono" value={rate} onChange={(e) => setRate(parseFloat(e.target.value) || 0)} data-testid="fx-rate" />
          </div>
          <span className="text-xs text-gray-500 mb-2">USD</span>
          {canEdit && <button className="ps-btn ps-btn-primary" onClick={save} data-testid="fx-save"><Save className="w-4 h-4" /> Post</button>}
        </div>
        <div className="text-[11px] text-gray-500 mt-3">
          FDAs auto-pick the rate matching the invoice month. Set it once per month.
          Pre-fills with your most recent posted rate.
        </div>
      </div>
      <div className="border border-gray-200 bg-white">
        <table className="ps-table" data-testid="fx-table"><thead><tr><th>Month</th><th>1 EUR = USD</th><th>Posted at</th><th>By</th></tr></thead><tbody>
          {items.map((f) => (<tr key={f.month}><td className="font-mono font-bold">{f.month}</td><td className="font-mono">{f.eur_to_usd}</td><td className="font-mono text-xs">{f.posted_at?.slice(0, 19)}</td><td className="text-xs">{f.posted_by}</td></tr>))}
          {items.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-gray-400">No FX rates posted yet.</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}

// ─── FDA GENERATION ──────────────────────────────────────────────
function FdaTab() {
  const { user } = useAuth();
  // Roles that can save + email an FDA. Aligned with backend
  // ROLES_RENTALS_FDA = (admin, general_manager, finance); supervisor kept
  // for continuity with legacy users who reviewed FDAs before this role-gate
  // tightening.
  const canEdit = ["admin", "finance", "general_manager", "supervisor"].includes(user?.role);
  const [customer, setCustomer] = useState("");
  const [yyyyMm, setYyyyMm] = useState(new Date().toISOString().slice(0, 7));
  const [partners, setPartners] = useState([]);
  const [fda, setFda] = useState(null);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null); // { to, subject, body, cc }

  useEffect(() => {
    api.get("/rentals/tariffs")
      .then((r) => setPartners(r.data.map((t) => ({ code: t.customer_code, legal_name: t.customer_code }))))
      .catch(() => {});
  }, []);

  const generate = async () => {
    if (!customer || !yyyyMm) { toast.error("Pick a customer and month"); return; }
    setLoading(true); setFda(null); setSavedInvoiceId(null);
    try {
      const { data } = await api.get(`/rentals/generate-fda?customer=${customer}&yyyy_mm=${yyyyMm}`);
      setFda(data);
      // If this period has already been invoiced, flip the Finalize button to
      // its "Invoiced ✓" state immediately so the user cannot accidentally
      // double-submit. The invoice_no on a saved RENTAL invoice matches
      // fda_no exactly.
      try {
        const invs = await api.get(`/invoices?carrier_code=${encodeURIComponent(customer)}&invoice_type=RENTAL&limit=50`);
        const match = (invs.data || []).find((inv) => inv.invoice_no === data.fda_no);
        if (match) setSavedInvoiceId(match.id);
      } catch (_) { /* non-fatal: button just defaults to Finalize */ }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // Inline helper so the FX warning banner can post a rate without
  // navigating away from the half-generated FDA.
  const postFxInline = async () => {
    if (!fda?.period) return;
    const last = window.prompt(
      `Post EUR → USD rate for ${fda.period}?\n` +
      `Enter the ECB mid-rate for the invoice month (e.g. 1.0825).`,
      "1.0825",
    );
    if (!last) return;
    const parsed = parseFloat(last);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Rate must be a positive number");
      return;
    }
    try {
      await api.put(`/rentals/fx-rates/${fda.period}`, { month: fda.period, eur_to_usd: parsed });
      toast.success(`${fda.period} → ${parsed} — regenerating FDA…`);
      await generate();  // refresh the preview so USD column populates
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    if (!fda) return;
    setSaving(true);
    try {
      // The backend is idempotent — same customer+month returns the existing
      // invoice record. We detect that by comparing `saved_at` vs `created_at`
      // (an existing invoice has an older `saved_at`, a fresh one has today's).
      const { data } = await api.post("/rentals/save-fda", fda);
      setSavedInvoiceId(data.id);
      const todayIso = new Date().toISOString().slice(0, 10);
      const freshlyCreated = (data.created_at || "").slice(0, 10) === todayIso;
      if (freshlyCreated) {
        toast.success(`Invoice ${fda.fda_no} created — now visible in the Invoices list.`);
      } else {
        toast.info(`This FDA was already invoiced as ${fda.fda_no} on ${(data.saved_at || data.created_at || "").slice(0, 10)}. Opening the existing record.`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const openEmail = async () => {
    if (!fda) return;
    let invoiceId = savedInvoiceId;
    if (!invoiceId) {
      // Auto-save first
      setSaving(true);
      try {
        const { data } = await api.post("/rentals/save-fda", fda);
        invoiceId = data.id;
        setSavedInvoiceId(invoiceId);
      } catch (e) { toast.error(formatApiError(e)); setSaving(false); return; }
      finally { setSaving(false); }
    }
    // Customer-facing currency: EUR for Greenport / Liner Agencies, USD elsewhere.
    const headlineAmt = fda.currency === "EUR"
      ? Number(fda.totals.grand_total_eur ?? 0).toFixed(2)
      : Number(fda.totals.grand_total).toFixed(2);
    setEmailDialog({
      invoiceId,
      to: fda.billing_email || "",
      cc: fda.billing_cc || "",
      // Snapshot of what the partner had at open — used to decide whether
      // to show/enable the "Save for next time" checkbox.
      partnerDefaults: { to: fda.billing_email || "", cc: fda.billing_cc || "" },
      customer_code: fda.customer_code,
      saveDefaults: false,
      subject: `Portside ${fda.fda_no} — ${fda.customer_name} — ${fda.currency} ${headlineAmt}`,
      body: `Dear partner,\n\nPlease find attached the rental activity statement (FDA) for ${fda.period}.\n\nCustomer: ${fda.customer_name}\nTanks billed: ${fda.totals.line_count}\nGrand total: ${fda.currency} ${headlineAmt}\n\nKind regards,\nPortside Operations`,
    });
  };

  const sendEmail = async () => {
    if (!emailDialog || !fda) return;
    if (!emailDialog.to.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDialog.to.trim())) {
      toast.error("Please provide a valid recipient email");
      return;
    }
    setEmailing(true);
    try {
      const blob = await fdaPdfBlob(fda);
      const fd = new FormData();
      fd.append("to", emailDialog.to.trim());
      if (emailDialog.cc.trim()) fd.append("cc", emailDialog.cc.trim());
      fd.append("subject", emailDialog.subject);
      fd.append("body", emailDialog.body);
      fd.append("file", blob, `${fda.fda_no}.pdf`);
      const { data } = await api.post(`/invoices/${emailDialog.invoiceId}/email`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.skipped) toast.warning("SMTP not configured — email logged but not sent");
      else toast.success(`FDA emailed to ${emailDialog.to.trim()}`);

      // Persist the operator's addresses back to the customer record so the
      // next FDA for this customer pre-fills without re-typing.
      if (emailDialog.saveDefaults && emailDialog.customer_code) {
        const trimmedTo = emailDialog.to.trim();
        const trimmedCc = emailDialog.cc.trim();
        const defaults = emailDialog.partnerDefaults || {};
        if (trimmedTo !== (defaults.to || "") || trimmedCc !== (defaults.cc || "")) {
          try {
            await api.patch(`/partners/${encodeURIComponent(emailDialog.customer_code)}`, {
              billing_email: trimmedTo,
              billing_cc: trimmedCc || null,
            });
            toast.info(`Saved for future ${emailDialog.customer_code} FDAs`);
          } catch (e) {
            toast.warning("Email sent, but couldn't save as default — check partner permissions.");
          }
        }
      }
      setEmailDialog(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailing(false); }
  };

  const exportCsv = () => {
    if (!fda) return;
    downloadCsv(fda.lines, `${fda.fda_no}`, [
      { key: "tank_number", label: "Tank" },
      { key: "pickup_date", label: "Pickup" },
      { key: "drop_off_date", label: "Drop-off" },
      { key: "rental_days_in_month", label: "Days" },
      { key: "daily_rate", label: "Rate" },
      { key: "rental_usd", label: "Rental USD" },
      { key: "handling_usd", label: "Handling USD" },
      { key: "mobilisation_usd", label: "Mobilisation USD" },
      { key: "cleaning_usd", label: "Cleaning USD" },
      { key: "trucking_usd", label: "Trucking USD" },
      { key: "line_total_usd", label: "Total USD" },
    ]);
  };

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 bg-white p-5 flex gap-3 items-end flex-wrap">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Customer *</label>
          <select className="ps-input font-mono w-56" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="fda-customer">
            <option value="">— customer —</option>
            {partners.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Month *</label>
          <input type="month" className="ps-input font-mono" value={yyyyMm} onChange={(e) => setYyyyMm(e.target.value)} data-testid="fda-month" />
        </div>
        <button className="ps-btn ps-btn-primary" onClick={generate} disabled={loading} data-testid="fda-generate">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><FileText className="w-4 h-4" /> Generate FDA</>}
        </button>
        {fda && <button className="ps-btn ps-btn-secondary" onClick={() => downloadFdaPdf(fda)} data-testid="fda-pdf"><Printer className="w-4 h-4" /> PDF</button>}
        {fda && <button className="ps-btn ps-btn-secondary" onClick={exportCsv} data-testid="fda-csv"><FileSpreadsheet className="w-4 h-4" /> CSV</button>}
        {fda && canEdit && (
          <button className="ps-btn ps-btn-primary" onClick={save} disabled={saving || !!savedInvoiceId} data-testid="fda-save" title="Create a RENTAL invoice record from this FDA so it appears in the Invoices list. One invoice per customer per month.">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Finalizing…</> : savedInvoiceId ? <><Save className="w-4 h-4" /> Invoiced ✓</> : <><Save className="w-4 h-4" /> Finalize & Invoice</>}
          </button>
        )}
        {fda && canEdit && (
          <button className="ps-btn ps-btn-primary" onClick={openEmail} disabled={emailing} data-testid="fda-email">
            <Mail className="w-4 h-4" /> Save & Email
          </button>
        )}
      </div>

      {emailDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEmailDialog(null)}>
          <div className="bg-white border border-gray-200 max-w-xl w-full" onClick={(e) => e.stopPropagation()} data-testid="fda-email-dialog">
            <div className="px-5 py-3 border-b border-gray-200">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Email FDA</div>
              <div className="font-mono font-bold text-sm mt-0.5">{fda.fda_no}</div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">To *</label>
                <input className="ps-input font-mono" value={emailDialog.to} onChange={(e) => setEmailDialog({ ...emailDialog, to: e.target.value })} placeholder="ar@portside.nl or customer@…" data-testid="fda-email-to" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">CC (comma-separated)</label>
                <input className="ps-input font-mono" value={emailDialog.cc} onChange={(e) => setEmailDialog({ ...emailDialog, cc: e.target.value })} placeholder="optional" data-testid="fda-email-cc" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Subject</label>
                <input className="ps-input" value={emailDialog.subject} onChange={(e) => setEmailDialog({ ...emailDialog, subject: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Body</label>
                <textarea className="ps-input text-xs font-mono" rows={6} value={emailDialog.body} onChange={(e) => setEmailDialog({ ...emailDialog, body: e.target.value })} />
              </div>
              <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-3 py-2">
                Attachment: <span className="font-mono">{fda.fda_no}.pdf</span>
              </div>
              {(() => {
                const t = (emailDialog.to || "").trim();
                const c = (emailDialog.cc || "").trim();
                const d = emailDialog.partnerDefaults || {};
                const changed = t && (t !== (d.to || "") || c !== (d.cc || ""));
                if (!changed || !emailDialog.customer_code) return null;
                return (
                  <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer bg-blue-50 border border-blue-200 px-3 py-2 rounded-sm" data-testid="fda-save-defaults-row">
                    <input
                      type="checkbox"
                      checked={!!emailDialog.saveDefaults}
                      onChange={(e) => setEmailDialog({ ...emailDialog, saveDefaults: e.target.checked })}
                      className="mt-0.5"
                      data-testid="fda-save-defaults"
                    />
                    <span>
                      Save these addresses on <span className="font-mono font-bold">{emailDialog.customer_code}</span> so the next FDA for this customer pre-fills automatically.
                    </span>
                  </label>
                );
              })()}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button className="ps-btn ps-btn-secondary" onClick={() => setEmailDialog(null)} disabled={emailing}>Cancel</button>
              <button className="ps-btn ps-btn-primary" onClick={sendEmail} disabled={emailing} data-testid="fda-email-send">
                {emailing ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4" /> Send</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {fda && (
        <div className="space-y-4" data-testid="fda-result">
          {fda.fx_warning && (
            <div className="border-l-4 border-yellow-400 bg-yellow-50 px-4 py-3 text-xs flex items-start gap-2" data-testid="fda-fx-warning">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                FX warning — some EUR amounts could not be converted to USD because no FX rate is posted for <b>{fda.period}</b>.
              </div>
              {canEdit && (
                <button
                  onClick={postFxInline}
                  className="text-[11px] font-bold uppercase tracking-wider text-yellow-900 hover:text-yellow-700 underline whitespace-nowrap"
                  data-testid="fda-post-fx-inline"
                >
                  Post rate →
                </button>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-gray-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">FDA</div>
              <div className="font-mono font-bold text-sm mt-1">{fda.fda_no}</div>
            </div>
            <div className="border border-gray-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Customer</div>
              <div className="font-mono font-bold text-sm mt-1">{fda.customer_name}</div>
              {fda.billing_email && <div className="text-[10px] text-gray-500">{fda.billing_email}</div>}
            </div>
            <div className="border border-gray-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Tanks billed</div>
              <div className="font-mono font-black text-2xl mt-1 text-[#006E9E]">{fda.totals.line_count}</div>
            </div>
            <div className="border border-gray-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Grand total</div>
              {fda.currency === "EUR" ? (
                <>
                  <div className="font-mono font-black text-2xl mt-1 text-[#99D420]">
                    EUR {Number(fda.totals.grand_total_eur ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                    ≈ USD {Number(fda.totals.grand_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-gray-400">(stats only)</span>
                  </div>
                </>
              ) : (
                <div className="font-mono font-black text-2xl mt-1 text-[#99D420]">
                  USD {Number(fda.totals.grand_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              )}
            </div>
          </div>
          <div className="border border-gray-200 bg-white overflow-x-auto">
            <table className="ps-table">
              <thead><tr><th>Tank</th><th>Pickup</th><th>Drop-off</th><th className="text-right">Days</th><th className="text-right">Rental</th><th className="text-right">Handling</th><th className="text-right">Mob.</th><th className="text-right">Cleaning</th><th className="text-right">Trucking</th><th className="text-right">Total {fda.currency}</th></tr></thead>
              <tbody>
                {fda.lines.map((ln) => {
                  const isEur = fda.currency === "EUR";
                  const sym = isEur ? "€" : "$";
                  const fx = fda.eur_to_usd || 1;
                  // Native-currency amounts:
                  //  - rental line: either daily rate or lump-sum (already EUR)
                  //  - services lines: stored USD → convert back to EUR for display
                  const rentalNative = isEur
                    ? ((ln.rental_native || 0) + (ln.lumpsum_native_eur || 0))
                    : (ln.rental_usd || 0);
                  const cvt = (usd) => isEur ? (fx ? (usd || 0) / fx : 0) : (usd || 0);
                  const lineTotalNative = isEur
                    ? (rentalNative + cvt(ln.handling_usd) + cvt(ln.mobilisation_usd) + cvt(ln.cleaning_usd) + cvt(ln.trucking_usd))
                    : (ln.line_total_usd || 0);
                  return (
                    <tr key={ln.tank_number}>
                      <td className="font-mono font-bold">{ln.tank_number}</td>
                      <td className="font-mono text-xs">{ln.pickup_date || "—"}</td>
                      <td className="font-mono text-xs">{ln.orphan_services_only ? "—" : (ln.drop_off_date || <span className="text-[#99D420]">on-hire</span>)}</td>
                      <td className="font-mono text-right">
                        {ln.rental_days_in_month}
                        {ln.rental_billable_from && (
                          <div className="text-[9px] text-amber-700" title={`${ln.free_days} free days from pickup — billing starts ${ln.rental_billable_from}`}>
                            bills from {ln.rental_billable_from}
                          </div>
                        )}
                        {(ln.days_at_step_down_rate || 0) > 0 && (
                          <div className="text-[9px] text-[#006E9E]" title={`Step-down to ${sym}${ln.step_down_daily_rate}/day after ${ln.step_down_after_days} days from pickup (from ${ln.step_down_from})`}>
                            {ln.days_at_initial_rate}×{sym}{ln.daily_rate} + {ln.days_at_step_down_rate}×{sym}{ln.step_down_daily_rate}
                          </div>
                        )}
                      </td>
                      <td className="font-mono text-right">{sym}{rentalNative.toFixed(2)}</td>
                      <td className="font-mono text-right">{sym}{cvt(ln.handling_usd).toFixed(2)}</td>
                      <td className="font-mono text-right">{sym}{cvt(ln.mobilisation_usd).toFixed(2)}</td>
                      <td className="font-mono text-right">{sym}{cvt(ln.cleaning_usd).toFixed(2)}</td>
                      <td className="font-mono text-right">{sym}{cvt(ln.trucking_usd).toFixed(2)}</td>
                      <td className="font-mono text-right font-bold">{sym}{lineTotalNative.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(fda.offhire_lines || []).length > 0 && (
            <div className="border border-amber-200 bg-amber-50/50 overflow-x-auto" data-testid="fda-offhire-section">
              <div className="px-4 py-2 border-b border-amber-200 bg-amber-100/70 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-amber-900">
                  Off-hire costs ({fda.offhire_lines.length})
                </span>
                <span className="font-mono text-sm font-bold text-amber-900">
                  {fda.currency === "EUR"
                    ? `€${Number(fda.totals.offhire_total_eur ?? 0).toFixed(2)}`
                    : `$${Number(fda.totals.offhire_total_usd ?? 0).toFixed(2)}`}
                </span>
              </div>
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Tank</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Vendor</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {fda.offhire_lines.map((ln) => (
                    <tr key={ln.id}>
                      <td className="font-mono text-xs">{ln.incurred_at}</td>
                      <td className="font-mono font-bold">{ln.tank_number}</td>
                      <td className="font-semibold">{ln.category}</td>
                      <td className="text-xs">{ln.description}</td>
                      <td className="text-xs text-gray-600">{ln.vendor || "—"}</td>
                      <td className="text-right font-mono font-bold">
                        {ln.invoice_currency === "EUR" ? "€" : "$"}
                        {Number(ln.invoice_amount_native || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
